import { useQuery } from '@tanstack/react-query'
import { getCurrencySymbol, getProjectCurrencySign, t } from '@stokr/components-library'
import type { BtcPrice } from 'api/prices'
import type { CopyKey } from 'locales/copy-keys.gen'
import { useBtcPrice } from 'hooks/useBtcPrice'
import type { Project, ProjectTradingData, TradingOption } from 'models/project'
import { pickContent, StoContentTypes } from 'models/projectContent'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import type { Exchange30DayPrice, ExchangeOrder, TradingMarketData } from 'models/trading'
import { generateSideSwapTradingLink } from 'utils/generateSideSwapTradingLink'
import { useStoProject } from './useStoProject'
import { useTradeEligibility } from './useEligibility'
import { stoQueries } from './queries/stoQueries'

export interface TradingExchange {
  source: string
  name: string
  logo: string
}

export interface TradingVenue extends TradingExchange {
  pairs: string[]
  url: string
}

/** A table value; `approx` is the fiat conversion shown under a BTC value. */
export interface MarketCell {
  value: string
  approx?: string
}

export interface MarketRow {
  key: string
  pair: { label: string; url: string }
  low: MarketCell
  high: MarketCell
  last: MarketCell
  change?: number
  volume: MarketCell
  totalTokens: MarketCell
  marketCap: MarketCell
}

export interface MarketTable extends TradingExchange {
  rows: MarketRow[]
}

export interface OrderBookCard {
  key: string
  tradingPair: string
  url: string
  bidAmount: string
  bidPrice: string
  askPrice: string
  askAmount: string
}

export interface OrderBookGroup extends TradingExchange {
  cards: OrderBookCard[]
}

export interface TradingView {
  venues: TradingVenue[]
  marketTables: MarketTable[]
  orderBooks: OrderBookGroup[]
}

const EXCHANGE_LOGOS: Record<string, string> = {
  sideswap:
    'https://res.cloudinary.com/stokr/image/upload/v1715108248/STO%20details/Blockstream-mining/Sideswap_logo_mjyk1i.svg?react',
  bitfinex:
    'https://res.cloudinary.com/stokr/image/upload/v1646225935/STO%20details/Blockstream-mining/Bitfinex_Securities_Logo_Colored_kq9uts.svg?react',
}

const EXCHANGE_NAMES: Record<string, string> = {
  sideswap: 'SideSwap',
  bitfinex: 'Bitfinex Securities',
}

const isUsdPair = (pair?: string) => Boolean(pair?.toUpperCase().includes('USD'))

const getOptionForSource = (options: TradingOption[], source: string, preferUsd = false): TradingOption | undefined => {
  const forSource = options.filter((option) => option.source === source)
  if (forSource.length === 0) return undefined
  const usd = forSource.find((option) => isUsdPair(option.tradingPair))
  const nonUsd = forSource.find((option) => !isUsdPair(option.tradingPair))
  if (preferUsd) return usd ?? forSource[0]
  return nonUsd ?? (usd ? undefined : forSource[0])
}

const bitfinexLink = (project: Project, currency: string) =>
  `https://trading.bitfinex.com/t/${project.tokenSymbol}:${currency}`

const fallbackLink = (project: Project, source: string, currency = '') =>
  source === 'sideswap' ? generateSideSwapTradingLink(project) : bitfinexLink(project, currency.toUpperCase())

const toExchange = (source: string, options: TradingOption[]): TradingExchange => ({
  source,
  name: EXCHANGE_NAMES[source] ?? source,
  logo: options.find((option) => option.source === source)?.icon || EXCHANGE_LOGOS[source] || '',
})

/** Shown before the others, whatever back-office's order. */
const LEADING_SOURCES = ['bitfinex']
const leadRank = (source: string) => {
  const index = LEADING_SOURCES.indexOf(source)
  return index === -1 ? LEADING_SOURCES.length : index
}

/** Bitfinex first, then the configured order, then any source only the feed knows about. */
const collectSources = (options: TradingOption[], market: TradingMarketData | undefined): string[] =>
  [
    ...new Set([
      ...options.map((option) => option.source),
      ...(market?.prices ?? []).map((price) => price.source),
      ...(market?.orderBook ?? []).map((order) => order.source),
    ]),
  ]
    .filter((source): source is string => Boolean(source))
    .sort((a, b) => leadRank(a) - leadRank(b))

const toVenues = (
  sources: string[],
  options: TradingOption[],
  orderBook: ExchangeOrder[],
  project: Project,
): TradingVenue[] =>
  sources.flatMap((source) => {
    const configured = options.filter((option) => option.source === source)
    const pairs = configured.length
      ? configured.map((option) => option.tradingPair).filter((pair): pair is string => Boolean(pair))
      : orderBook
          .filter((order) => order.source === source)
          .map((order) => `${project.tokenSymbol}/${order.currency?.toUpperCase()}`)
    if (pairs.length === 0 && configured.length === 0) return []
    const url =
      configured.find((option) => option.url)?.url ||
      fallbackLink(project, source, orderBook.find((o) => o.source === source)?.currency)
    return [{ ...toExchange(source, options), pairs, url }]
  })

/** `61,159.0000`: grouped thousands, a fixed number of decimals. */
const grouped = (value: number, digits: number): string =>
  value.toLocaleString('en', { minimumFractionDigits: digits, maximumFractionDigits: digits })

const fixed = (value: number | undefined, digits: number): string => (value ? grouped(value, digits) : '-')

const approx = (btcValue: number | undefined, rate: number, sign: string): string | undefined =>
  btcValue && rate
    ? `≈ ${sign} ${(btcValue * rate).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : undefined

const toMarketRows = (
  prices: Exchange30DayPrice,
  project: Project,
  options: TradingOption[],
  btcPrice: BtcPrice,
): MarketRow[] => {
  const tokenCurrency = project.tokenCurrency ?? ''
  const sign = getProjectCurrencySign(tokenCurrency)
  const rate = Number(btcPrice[tokenCurrency as keyof BtcPrice]) || 0
  const totalTokenAmount = project.totalTokenAmount ?? 0
  const totalTokens = { value: grouped(totalTokenAmount, project.tokenDecimals ?? 0) }
  const option = getOptionForSource(options, prices.source)
  // Sideswap only quotes against L-BTC.
  const quote =
    prices.currency === 'btc' ? (prices.source === 'sideswap' ? 'lbtc' : 'bitcoin') : (prices.currency as string)
  const marketCap = totalTokenAmount * (prices.lastInBTC ?? 0) || 0
  const hasLast = Boolean(prices.lastInBTC)

  const rows: MarketRow[] = [
    {
      key: `${prices.source}-${prices.currency}`,
      pair: {
        label: option?.tradingPair || `${project.tokenSymbol}/${getCurrencySymbol(quote)}`,
        url: option?.url || fallbackLink(project, prices.source, getCurrencySymbol(prices.currency)),
      },
      low: { value: fixed(prices.low30daysInBTC, 8), approx: approx(prices.low30daysInBTC, rate, sign) },
      high: { value: fixed(prices.high30daysInBTC, 8), approx: approx(prices.high30daysInBTC, rate, sign) },
      last: { value: fixed(prices.lastInBTC, 8), approx: approx(prices.lastInBTC, rate, sign) },
      change: hasLast ? prices.change30daysInPercent : undefined,
      volume: { value: hasLast ? fixed(prices.volume30daysInTokens, 8) : '-' },
      totalTokens,
      marketCap: { value: fixed(marketCap, 8), approx: approx(marketCap, rate, sign) },
    },
  ]

  if (prices.source === 'bitfinex' && prices.hasUSDData) {
    const usdOption = getOptionForSource(options, 'bitfinex', true)
    rows.push({
      key: `${prices.source}-usd`,
      pair: {
        label: usdOption?.tradingPair || `${project.tokenSymbol}/USD`,
        url: usdOption?.url || bitfinexLink(project, 'USD'),
      },
      low: { value: fixed(prices.low30daysInUSD, 2) },
      high: { value: fixed(prices.high30daysInUSD, 2) },
      last: { value: fixed(prices.lastInUSD, 2) },
      change: prices.change30daysInPercentUSD,
      volume: { value: fixed(prices.volume30daysInTokensUSD, 2) },
      totalTokens,
      marketCap: {
        value: `${sign}${grouped(totalTokenAmount * (prices.lastInUSD ?? 0), 2)}`,
      },
    })
  }

  return rows
}

const formatPrice = (price: number | null | undefined, currency?: string): string => {
  if (!price) return ''
  return currency === 'usd' ? `$${grouped(price, 2)}` : grouped(price, 8)
}

/** The option quoting this order's currency; the USD/non-USD split only as a fallback. */
const getOptionForOrder = (options: TradingOption[], source: string, currency = ''): TradingOption | undefined =>
  options.find(
    (option) => option.source === source && option.tradingPair?.split('/')[1]?.toUpperCase() === currency.toUpperCase(),
  ) ?? getOptionForSource(options, source, currency.toLowerCase() === 'usd')

/** One card per pair: a source can list several (e.g. bitfinex BMN2/BTC and BMN2/USD). */
const toOrderBookCard = (order: ExchangeOrder, options: TradingOption[], project: Project): OrderBookCard => {
  const source = order.source ?? ''
  const option = getOptionForOrder(options, source, order.currency)
  return {
    key: `${source}-${order.currency}`,
    tradingPair: option?.tradingPair || `${project.tokenSymbol}/${order.currency?.toUpperCase()}`,
    url: option?.url || fallbackLink(project, source, order.currency),
    bidAmount: order.bidAmount ? String(order.bidAmount) : t('trading.orderBook.noActiveBids'),
    bidPrice: formatPrice(order.bidPrice, order.currency),
    askPrice: formatPrice(order.askPrice, order.currency),
    askAmount: order.askAmount ? String(order.askAmount) : t('trading.orderBook.noActiveOffers'),
  }
}

/** Only options meant for the STO page that carry something to show. */
export const getVisibleTradingOptions = (content: ProjectTradingData | undefined): TradingOption[] =>
  (content?.tradingOptions ?? []).filter(
    (option) => option.showOnSTOPage !== false && Boolean(option.url || option.icon || option.tradingPair),
  )

export const toTradingView = (
  options: TradingOption[],
  market: TradingMarketData | undefined,
  btcPrice: BtcPrice | null | undefined,
  project: Project | undefined,
): TradingView => {
  if (!project) return { venues: [], marketTables: [], orderBooks: [] }
  const sources = collectSources(options, market)
  const orderBook = market?.orderBook ?? []

  // Values are converted through the BTC rate, so without it the table would be wrong, not just partial.
  const marketTables = btcPrice
    ? [...(market?.prices ?? [])]
        .sort((a, b) => leadRank(a.source ?? '') - leadRank(b.source ?? ''))
        .map((prices) => ({
          ...toExchange(prices.source, options),
          rows: toMarketRows(prices, project, options, btcPrice),
        }))
    : []

  const orderBooks = sources
    .map((source) => ({
      ...toExchange(source, options),
      cards: orderBook
        .filter((order) => order.source === source)
        .map((order) => toOrderBookCard(order, options, project)),
    }))
    .filter((group) => group.cards.length > 0)

  return { venues: toVenues(sources, options, orderBook, project), marketTables, orderBooks }
}

export const isTradingViewEmpty = (view: TradingView): boolean =>
  view.venues.length === 0 && view.marketTables.length === 0 && view.orderBooks.length === 0

export function useTrading(): SectionState<TradingView> {
  const { project, projectId } = useStoProject()
  const contentQuery = useQuery(stoQueries.content(projectId))
  const content = pickContent<ProjectTradingData>(contentQuery.data, StoContentTypes.TRADING)
  const declaredVisible = Boolean(content?.isTradingTabVisible)
  // Same gate as before: the market feed is only asked for when back-office shows the order book.
  const marketEnabled = declaredVisible && Boolean(content?.isOrderBookVisible)
  const marketQuery = useQuery(stoQueries.tradingMarket(project?.secondaryAssetId ?? undefined, marketEnabled))
  const btcPriceQuery = useBtcPrice({ enabled: marketEnabled })
  const options = getVisibleTradingOptions(content)

  return resolveSectionState({
    declaredVisible,
    isPending: contentQuery.isPending || marketQuery.isLoading || btcPriceQuery.isLoading,
    isError: contentQuery.isError,
    data: toTradingView(options, marketQuery.data, btcPriceQuery.data, project),
    isEmpty: isTradingViewEmpty,
  })
}

/** The venues the Trade tab offers, SideSwap first: the ones it has rules and copy for. */
const TRADE_VENUE_SOURCES = ['sideswap', 'bitfinex'] as const
export type TradeVenueSource = (typeof TRADE_VENUE_SOURCES)[number]

/** Figma 2897:20158 / 2897:20088: the line under each venue name. */
const VENUE_SUBTITLES: Record<TradeVenueSource, CopyKey> = {
  sideswap: 'stoPage.keyActions.trade.subtitle.sideswap',
  bitfinex: 'stoPage.keyActions.trade.subtitle.bitfinex',
}

export interface TradeNotice {
  variant: 'success' | 'warning'
  titleKey: CopyKey
}

/** One venue in the Trade tab: what it says and whether the visitor can trade there now. */
export interface TradeVenueAction {
  source: TradeVenueSource
  name: string
  /** "Peer to peer" / "Regulated". */
  subtitleKey: CopyKey
  url: string
  canTrade: boolean
  /** Why the button is off, shown as its tooltip. */
  lockedReasonKey?: CopyKey
  /** SideSwap only: STOKR eligibility is still loading, so neither state is shown yet. */
  isChecking: boolean
  /** Whether trading here needs STOKR's secondary-market eligibility (Bitfinex onboards on its own). */
  needsStokrEligibility: boolean
  notice?: TradeNotice
}

/** Configured venues, or none when back-office marks the product as not tradable. */
export const toTradeVenues = (
  content: ProjectTradingData | undefined,
  project: Project | undefined,
): TradingVenue[] => {
  if (!project || !content?.isAvailableForTrading) return []
  const options = getVisibleTradingOptions(content)
  const sources = TRADE_VENUE_SOURCES.filter((source) => options.some((option) => option.source === source))
  return toVenues(sources, options, [], project)
}

/**
 * Bitfinex runs its own onboarding, so it is always open. SideSwap needs the visitor to be
 * eligible for the secondary market on STOKR.
 */
export const toTradeVenueAction = (
  venue: TradingVenue,
  sideSwap: { isEligible: boolean; isResolving: boolean },
): TradeVenueAction => {
  const base = { name: venue.name, url: venue.url }
  // `toTradeVenues` lets through only the sources in TRADE_VENUE_SOURCES.
  const source = venue.source as TradeVenueSource
  if (source === 'bitfinex') {
    return {
      ...base,
      source,
      subtitleKey: VENUE_SUBTITLES.bitfinex,
      canTrade: true,
      isChecking: false,
      needsStokrEligibility: false,
      notice: { variant: 'success', titleKey: 'stoPage.keyActions.trade.bitfinex.onboarding' },
    }
  }
  const eligible = sideSwap.isEligible
  return {
    ...base,
    source,
    subtitleKey: VENUE_SUBTITLES.sideswap,
    canTrade: eligible,
    lockedReasonKey: eligible ? undefined : 'stoPage.keyActions.trade.sideswap.lockedReason',
    isChecking: sideSwap.isResolving,
    needsStokrEligibility: true,
    notice: eligible
      ? {
          variant: 'success',
          titleKey: 'stoPage.keyActions.trade.sideswap.completedTitle',
        }
      : {
          variant: 'warning',
          titleKey: 'stoPage.keyActions.trade.sideswap.requiredTitle',
        },
  }
}

/** The venue shown in the Trade tab: the one picked, else the first. */
export const pickTradeVenue = (
  venues: TradeVenueAction[],
  pickedSource: TradeVenueSource | undefined,
): TradeVenueAction | undefined => venues.find((venue) => venue.source === pickedSource) ?? venues[0]

export interface TradePanelState {
  isPending: boolean
  /** Empty when the product is not tradable. */
  venues: TradeVenueAction[]
}

export function useTradePanel(): TradePanelState {
  const { project, projectId, isPending } = useStoProject()
  const contentQuery = useQuery(stoQueries.content(projectId))
  const content = pickContent<ProjectTradingData>(contentQuery.data, StoContentTypes.TRADING)
  const sideSwap = useTradeEligibility(content)
  return {
    isPending: isPending || contentQuery.isPending,
    venues: toTradeVenues(content, project).map((venue) => toTradeVenueAction(venue, sideSwap)),
  }
}
